import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Activity,
  Gauge,
  LogOut,
  Menu,
  MoreHorizontal,
  Search,
  Settings,
  GraduationCap,
  Trophy,
  Users,
  ScrollText,
  X,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, refreshCsrf } from "../services/api";
import { toastSuccess, useUi } from "../stores/ui";
import { can } from "../types";
import { Button, Input } from "../components/ui";
import { LogActivityModal } from "../components/LogActivityModal";
import { useRealtime } from "../hooks/useRealtime";
import { cn } from "../utils/cn";
import { BRAND_HUB, BRAND_LOGO, BRAND_NAME } from "../brand";
import type { Permission } from "@hrhub/shared";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Users;
  permission?: Permission;
}

const memberNav: NavItem[] = [
  { to: "/", label: "Roster", icon: Users },
  { to: "/analytics", label: "Analytics", icon: Gauge, permission: "VIEW_ANALYTICS" },
  { to: "/recruits", label: "Recruits", icon: GraduationCap },
  { to: "/leaderboards", label: "Leaderboards", icon: Trophy },
];

const adminNav: NavItem[] = [
  { to: "/admin", label: "Admin", icon: Gauge },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText, permission: "VIEW_AUDIT_LOGS" },
  { to: "/admin/settings", label: "Settings", icon: Settings, permission: "MANAGE_SETTINGS" },
];

export function AppShell() {
  useRealtime();
  const user = useUi((s) => s.user)!;
  const setUser = useUi((s) => s.setUser);
  const open = useUi((s) => s.sidebarOpen);
  const setOpen = useUi((s) => s.setSidebarOpen);
  const navigate = useNavigate();
  const [logOpen, setLogOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [q, setQ] = useState("");
  const search = useQuery({
    queryKey: ["search", q],
    queryFn: () => api<{ results: { displayName: string; username: string; discordId?: string }[] }>(`/api/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
  });

  async function logout() {
    try {
      await api("/api/auth/logout", { method: "POST" });
      toastSuccess("Signed out");
    } catch {
      /* api() already shows an error toast */
    }
    refreshCsrf();
    setUser(null);
    navigate("/login");
  }

  const links = [...memberNav, ...(user.isManager ? adminNav : [])].filter(
    (item) => !item.permission || can(user, item.permission),
  );
  const primaryMobile = ["/", "/recruits", "/analytics", "/leaderboards"]
    .map((to) => links.find((item) => item.to === to))
    .filter((item): item is NavItem => Boolean(item))
    .slice(0, 3);
  const moreLinks = links.filter((item) => !primaryMobile.some((primary) => primary.to === item.to));

  function go(to: string) {
    setOpen(false);
    setMoreOpen(false);
    navigate(to);
  }

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[240px_1fr]">
      {open ? (
        <button
          type="button"
          className="fixed inset-0 z-20 bg-black/60 lg:hidden"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      ) : null}
      <aside className={cn("fixed inset-y-0 left-0 z-30 w-[min(18rem,86vw)] border-r border-white/10 bg-ink-900 p-4 transition lg:static lg:w-auto", open ? "translate-x-0" : "-translate-x-full lg:translate-x-0")}>
        <div className="mb-6 flex items-center gap-3">
          <img src={BRAND_LOGO} alt="" className="h-11 w-11 object-contain" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">{BRAND_NAME}</p>
            <p className="text-[11px] uppercase tracking-[0.16em] text-cyan">{BRAND_HUB}</p>
          </div>
          <button type="button" className="ml-auto rounded-md p-2 text-zinc-400 lg:hidden" aria-label="Close navigation" onClick={() => setOpen(false)}>
            <X size={16} />
          </button>
        </div>
        <nav className="space-y-0.5">
          {links.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/" || item.to === "/admin"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100",
                  isActive && "bg-white/[0.06] text-white",
                )
              }
            >
              <item.icon size={16} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-h-dvh flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-2 border-b border-white/10 bg-ink-950/95 px-3 py-2.5 lg:gap-3 lg:px-4 lg:py-3">
          <button className="focus-ring shrink-0 rounded-md p-2 lg:hidden" aria-label="Open navigation" onClick={() => setOpen(!open)}>
            <Menu size={18} />
          </button>
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-2.5 text-zinc-500" size={16} />
            <Input className="h-9 pl-9" placeholder="Search members" value={q} onChange={(e) => setQ(e.target.value)} />
            {search.data?.results?.length ? (
              <div className="absolute mt-2 max-h-64 w-full overflow-auto rounded-md border border-white/10 bg-ink-900 p-1">
                {search.data.results.map((result) => (
                  <button
                    key={result.username}
                    className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-white/[0.04]"
                    onClick={() => {
                      setQ("");
                      navigate(`/roster/${result.discordId ?? result.username}`);
                    }}
                  >
                    {result.displayName} <span className="text-zinc-500">@{result.username}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <Button className="shrink-0 px-2.5 sm:px-3.5" onClick={() => setLogOpen(true)}>
            <Activity size={16} />
            <span className="hidden sm:inline">Log Activity</span>
          </Button>
          <img src={user.avatarUrl} alt="" className="hidden h-8 w-8 shrink-0 rounded-full sm:block" />
          <button className="focus-ring hidden shrink-0 rounded-md p-2 text-zinc-400 sm:inline-flex" aria-label="Sign out" onClick={() => void logout()}>
            <LogOut size={16} />
          </button>
        </header>
        <main className="flex-1 px-3 py-4 pb-6 max-lg:pb-24 lg:px-8 lg:py-6">
          <Outlet />
        </main>
        <nav
          className="fixed inset-x-0 bottom-0 z-30 hidden border-t border-white/10 bg-ink-900/95 px-1 pb-[env(safe-area-inset-bottom)] pt-1 max-lg:grid"
          style={{ gridTemplateColumns: `repeat(${primaryMobile.length + 1}, minmax(0, 1fr))` }}
        >
          {primaryMobile.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => cn("flex flex-col items-center gap-1 py-2 text-[11px] text-zinc-400", isActive && "text-white")}
            >
              <item.icon size={16} />
              {item.label.split(" ")[0]}
            </NavLink>
          ))}
          <button type="button" className={cn("flex flex-col items-center gap-1 py-2 text-[11px] text-zinc-400", moreOpen && "text-white")} onClick={() => setMoreOpen(!moreOpen)}>
            <MoreHorizontal size={16} />
            More
          </button>
        </nav>
        {moreOpen ? (
          <div className="fixed inset-0 z-40 lg:hidden">
            <button type="button" className="absolute inset-0 bg-black/60" aria-label="Close menu" onClick={() => setMoreOpen(false)} />
            <div className="absolute inset-x-0 bottom-0 rounded-t-xl border-t border-white/10 bg-ink-900 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-zinc-500">More</p>
              <div className="grid gap-1">
                {moreLinks.map((item) => (
                  <button
                    key={item.to}
                    type="button"
                    className="flex items-center gap-3 rounded-md px-3 py-3 text-left text-sm text-zinc-200 hover:bg-white/[0.04]"
                    onClick={() => go(item.to)}
                  >
                    <item.icon size={16} />
                    {item.label}
                  </button>
                ))}
                <button type="button" className="flex items-center gap-3 rounded-md px-3 py-3 text-left text-sm text-zinc-200 hover:bg-white/[0.04]" onClick={() => void logout()}>
                  <LogOut size={16} />
                  Sign out
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <LogActivityModal open={logOpen} onClose={() => setLogOpen(false)} />
    </div>
  );
}
